/**
 * PANTALLA DE ENTRADA
 *
 * Sin registro, sin login social y sin «crear cuenta»: los usuarios se dan
 * de alta administrativamente y eso no va a cambiar en este incremento.
 *
 * El error es siempre el mismo —«Correo o contraseña incorrectos»— porque
 * lo es en la API: distinguir «ese correo no existe» de «la contraseña
 * está mal» convierte el login en un comprobador de direcciones.
 */
import { useState, type FormEvent, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { FalloApi, type Usuario } from '../api.ts';
import { Marca } from '../componentes/shell.tsx';
import { esOperarioDeTrabajo } from './mis-trabajos.tsx';
import { useBranding, useSesion } from '../contexto.tsx';

/**
 * PORTAL-DEBT-01 (DEBT-ZERO-01): `/clientes` como destino fijo daba un
 * 403 en blanco a quien no tuviera `clientes:leer` — CLIENTE siempre, y
 * OPERARIO también (PERMISOS_POR_ROL: []). Nunca se preguntaba "¿es
 * ADMIN?" en ningún sitio de este código (ver `shell.tsx`) y este destino
 * no iba a ser la excepción: se resuelve por PERMISO, no por rol —
 * `clientes:leer` (interno) → `/clientes`; `ots:leer_propias` (CLIENTE) →
 * `/mis-ots`; si no hay ninguno de los dos (OPERARIO hoy) → `/`, la única
 * pantalla que Inicio garantiza sin exigir permiso alguno.
 */
function destinoTrasEntrar(puede: (p: string) => boolean, usuario: Usuario): string {
  //  F10.3: el operario entra directamente en su trabajo, en línea — nunca en una portada genérica.
  if (esOperarioDeTrabajo(usuario.roles, puede)) return '/mis-ots';
  //  F10.1: quien gestiona OT de Limpieza aterriza en Inicio, que abre con su día («Hoy»).
  if (puede('ots:leer') && puede('clientes:leer')) return '/';
  if (puede('clientes:leer')) return '/clientes';
  if (puede('ots:leer_propias')) return '/mis-ots';
  //  FASE 8.2: el técnico de mantenimiento empieza por sus partes.
  if (puede('partes:ejecutar')) return '/mantenimiento/mis-partes';
  if (puede('activos:leer')) return '/activos';
  return '/';
}

export function PaginaLogin(): ReactNode {
  const b = useBranding();
  const { usuario, entrar, puede } = useSesion();
  const ubicacion = useLocation();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [camposMal, setCamposMal] = useState<Record<string, string>>({});

  if (usuario !== null) {
    const destino = usuario.requiereCambioPwd
      ? '/cambiar-password'
      : ((ubicacion.state as { desde?: string } | null)?.desde ?? destinoTrasEntrar(puede, usuario));
    return <Navigate to={destino} replace />;
  }

  async function enviar(e: FormEvent): Promise<void> {
    e.preventDefault();
    setEnviando(true);
    setError(null);
    setCamposMal({});
    try {
      //  Sin `navegar(...)` aquí: `entrar()` actualiza `usuario` en el
      //  contexto, el re-render que sigue hace `usuario !== null` arriba
      //  y ese `<Navigate>` —con el `puede` YA actualizado, nunca el de
      //  este cierre (que seguiría viendo al usuario anterior, null)—
      //  es quien decide el destino. Llamarlo aquí a mano evaluaría
      //  `puede` contra el `usuario` de ANTES de entrar.
      await entrar(email, password);
    } catch (e2) {
      if (e2 instanceof FalloApi) {
        setError(e2.message);
        setCamposMal(e2.erroresDeCampo ?? {});
      } else {
        setError('No se ha podido entrar.');
      }
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="login">
      <div className="login-caja">
        <div className="login-marca">
          <Marca />
          {b.lema !== '' && <div className="lema">{b.lema}</div>}
        </div>

        <form
          onSubmit={(e) => {
            void enviar(e);
          }}
          noValidate
        >
          {error !== null && (
            <div className="nota error" role="alert">
              {error}
            </div>
          )}

          <div className="campo">
            <label htmlFor="email">Correo</label>
            <input
              id="email"
              name="email"
              type="email"
              autoComplete="username"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              aria-invalid={camposMal['email'] !== undefined}
            />
            {camposMal['email'] !== undefined && (
              <div className="error-campo">{camposMal['email']}</div>
            )}
          </div>

          <div className="campo">
            <label htmlFor="password">Contraseña</label>
            <input
              id="password"
              name="password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              aria-invalid={camposMal['password'] !== undefined}
            />
            {camposMal['password'] !== undefined && (
              <div className="error-campo">{camposMal['password']}</div>
            )}
          </div>

          <button type="submit" className="principal ancho" disabled={enviando}>
            {enviando ? 'Entrando…' : 'Entrar'}
          </button>
        </form>

        {b.organizacion !== '' && b.organizacion !== b.nombre && <div className="login-pie">{b.organizacion}</div>}
      </div>
    </div>
  );
}
